import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import '../../styles/Login.css';
import { API_URL } from '../../app/constants'

import {
  Box,
  TextField,
  Button,
  Typography,
  Container,
  Checkbox,
  FormControlLabel,
  // Link as MuiLink,
  CssBaseline,
  Alert,
} from '@mui/material';
import { styled } from '@mui/system';
import Logo from '../../assets/images/logo.png';
import BeatLoader from 'react-spinners/BeatLoader';

const MainContainer = styled(Box)({
  minHeight: '100vh',
  backgroundColor: '#FFFFFF',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  fontFamily: 'Manrope, sans-serif',
  padding: '20px',
  '@media (min-width: 769px)': {
    alignItems: 'flex-start',
    paddingTop: '120px',
  },
  '@media (max-width: 768px)': {
    alignItems: 'flex-start',
    paddingTop: '120px',
    padding: '120px 20px 40px',
  },
});

const StyledContainer = styled(Container)({
  maxWidth: '700px',
  width: '700px',
  fontFamily: 'Manrope, sans-serif',
});

const StyledForm = styled('form')({
  width: '100%',
  marginTop: '1.5rem',
  '@media (min-width: 1024px)': {
    marginTop: '1rem',
  },
});

const StyledTextField = styled(TextField)({
  width: '100%',
  marginBottom: '16px',
  '& .MuiOutlinedInput-root': {
    borderRadius: '4px',
    backgroundColor: '#FFFFFF',
    transition: 'all 0.2s',
    '& fieldset': {
      border: '1px solid #E5E7EB',
      borderRadius: '4px',
    },
    '&:hover': {
      backgroundColor: '#FFFFFF',
      '& fieldset': {
        borderColor: '#D1D5DB',
      },
    },
    '&.Mui-focused': {
      backgroundColor: '#FFFFFF',
      boxShadow: '0 0 0 2px rgba(147, 51, 234, 0.1)',
      '& fieldset': {
        borderColor: '#9333EA',
        borderWidth: '1px',
      },
    },
  },
  '& .MuiInputBase-input': {
    padding: '12px 14px',
    fontSize: '15px',
    height: '16px',
  },
  '& .MuiInputLabel-root': {
    fontSize: '14px',
  },
});

const FieldTitle = styled(Typography)({
  textAlign: 'left',
  marginBottom: '4px',
  fontWeight: '500',
  fontSize: '14px',
  color: '#374151',
  fontFamily: 'Manrope, sans-serif',
});

const StyledButton = styled(Button)({
  width: '100%',
  marginTop: '1rem',
  marginBottom: '1rem',
  backgroundColor: '#9333EA',
  color: 'white',
  borderRadius: '4px !important',
  border: '1px solid #9333EA',
  padding: '12px 14px',
  fontWeight: 600,
  fontSize: '15px',
  textTransform: 'none',
  letterSpacing: 0,
  fontFamily: 'Manrope, sans-serif',
  minHeight: '48px',
  boxShadow: 'none !important',
  '&.MuiButton-root': {
    borderRadius: '4px !important',
  },
  '&:hover': {
    backgroundColor: '#7928CA',
    border: '1px solid #7928CA',
    boxShadow: 'none !important',
    borderRadius: '4px !important',
  },
  '&.Mui-disabled': {
    backgroundColor: '#E9D5FF',
    color: '#fff',
    border: '1px solid #E9D5FF',
    borderRadius: '4px !important',
  },
});

export default function SignUp() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    agreeToAll: false,
    termsOfUse: false,
    privacyPolicy: false,
    marketingConsent: false,
  });
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (event) => {
    const { name, value, checked, type } = event.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: type === 'checkbox' || type === 'radio' ? checked : value
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    // Check if all fields are filled
    if (!formData.firstName || !formData.lastName || !formData.username || !formData.email || !formData.password) {
      setErrorMessage('All fields are required.');
      return;
    }

    // Check if passwords match
    if (formData.password !== formData.confirmPassword) {
      setErrorMessage('Passwords do not match.');
      return;
    }

    // Check if Terms and Conditions are accepted
    if (!formData.marketingConsent) {
      setErrorMessage('You must agree to the Terms and Conditions and Privacy Policy to continue.');
      return;
    }

    setLoading(true);
    setErrorMessage('');

    const payload = {
      email: formData.email,
      firstname: formData.firstName,
      lastname: formData.lastName,
      level: "", // Empty so it can be set during the target level step
      password: formData.password,
      username: formData.username
    };

    try {
      const response = await fetch(`${API_URL}/api/v1/auth/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const responseData = await response.json();
      setLoading(false);

      if (response.ok) {
        localStorage.setItem('jwt', responseData.access);
        console.log('Registration successful:', responseData);
        navigate('/email-verification'); // Redirect to email verification page
      } else {
        // Handle specific error messages
        const errorMsg = responseData.message || responseData.error || 'Failed to register';
        
        if (errorMsg.toLowerCase().includes('username') && errorMsg.toLowerCase().includes('already')) {
          setErrorMessage('Username already exists, choose another one.');
        } else if (errorMsg.toLowerCase().includes('email') && errorMsg.toLowerCase().includes('already')) {
          setErrorMessage('Email already exists, choose another one.');
        } else if (errorMsg.toLowerCase().includes('username') && errorMsg.toLowerCase().includes('exist')) {
          setErrorMessage('Username already exists, choose another one.');
        } else if (errorMsg.toLowerCase().includes('email') && errorMsg.toLowerCase().includes('exist')) {
          setErrorMessage('Email already exists, choose another one.');
        } else {
          setErrorMessage(errorMsg);
        }
      }
    } catch (error) {
      setLoading(false);
      setErrorMessage('Error during registration: ' + error.message);
    }
  };

  return (
    <>
      <CssBaseline />
      <Box className="logo-container-box">
        <img src={Logo} alt="Mina Logo" className="mina-logo" />
      </Box>
      <MainContainer>
        <StyledContainer component="main">
            <Typography 
              component="h1" 
              variant="h4" 
              sx={{ 
                textAlign: 'center',
                fontWeight: 700, 
                fontSize: '1.8rem', 
                color: '#111827',
                marginBottom: '32px',
                fontFamily: 'Manrope, sans-serif'
              }}
            >
              Create Account
            </Typography>
          <Box sx={{ textAlign: 'center', marginBottom: '32px' }}>
            <Typography 
              sx={{ 
                color: '#64748b',
                fontSize: '1rem',
                fontFamily: 'Manrope, sans-serif'
              }}
            >
              Start your JLPT training journey today!
            </Typography>
          </Box>
          {errorMessage && (
            <Alert severity="error" sx={{ width: '100%', marginBottom: 2 }}>
              {errorMessage}
            </Alert>
          )}
          <StyledForm onSubmit={handleSubmit}>
            <Box sx={{ display: 'flex', gap: '16px', '@media (max-width: 768px)': { flexDirection: 'column', gap: 0 } }}>
              <Box sx={{ flex: 1 }}>
                <FieldTitle>First Name</FieldTitle>
                <StyledTextField
                  required
                  fullWidth
                  id="firstName"
                  name="firstName"
                  autoComplete="given-name"
                  autoFocus
                  value={formData.firstName}
                  onChange={handleChange}
                  placeholder="e.g. John"
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <FieldTitle>Last Name</FieldTitle>
                <StyledTextField
                  required
                  fullWidth
                  id="lastName"
                  name="lastName"
                  autoComplete="family-name"
                  value={formData.lastName}
                  onChange={handleChange}
                  placeholder="e.g. Smith"
                />
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', gap: '16px', '@media (max-width: 768px)': { flexDirection: 'column', gap: 0 } }}>
              <Box sx={{ flex: 1 }}>
                <FieldTitle>Username</FieldTitle>
                <StyledTextField
                  required
                  fullWidth
                  id="username"
                  name="username"
                  autoComplete="username"
                  value={formData.username}
                  onChange={handleChange}
                  placeholder="e.g. johnsmith123"
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <FieldTitle>Email Address</FieldTitle>
                <StyledTextField
                  required
                  fullWidth
                  id="email"
                  name="email"
                  autoComplete="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="e.g. john@example.com"
                />
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 2, '@media (max-width: 768px)': { flexDirection: 'column', gap: 0 } }}>
              <Box sx={{ flex: 1 }}>
                <FieldTitle>Password</FieldTitle>
                <StyledTextField
                  required
                  fullWidth
                  name="password"
                  type="password"
                  id="password"
                  autoComplete="new-password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="e.g. MyPassword123!"
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <FieldTitle>Confirm Password</FieldTitle>
                <StyledTextField
                  required
                  fullWidth
                  name="confirmPassword"
                  type="password"
                  id="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="e.g. MyPassword123!"
                />
              </Box>
            </Box>
            <Box sx={{ 
              padding: '12px', 
              border: '1px solid #E5E7EB', 
              borderRadius: '4px', 
              backgroundColor: '#FFFFFF',
              marginTop: '16px',
              marginBottom: '16px'
            }}>
              <FormControlLabel
                control={
                  <Checkbox 
                    checked={formData.marketingConsent} 
                    onChange={handleChange} 
                    name="marketingConsent"
                    sx={{
                      color: '#D1D5DB',
                      '&.Mui-checked': {
                        color: '#9333EA',
                      },
                    }}
                  />
                }
                label={
                  <Typography sx={{ 
                    fontSize: '14px', 
                    color: '#4B5563',
                    fontFamily: 'Manrope, sans-serif'
                  }}>
                    I agree to the{' '}
                    <a 
                      href="https://getmina.co/terms" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{ 
                        color: '#9333EA', 
                        textDecoration: 'none',
                        fontWeight: '500'
                      }}
                    >
                      Terms and Conditions
                    </a>
                    {' '}and{' '}
                    <a 
                      href="https://getmina.co/privacy" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{ 
                        color: '#9333EA', 
                        textDecoration: 'none',
                        fontWeight: '500'
                      }}
                    >
                      Privacy Policy
                    </a>
                  </Typography>
                }
              />
            </Box>
            <StyledButton
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading 
                // || !formData.termsOfUse || !formData.privacyPolicy

              }
            >
              {loading ? <BeatLoader color="#fff" size={10} /> : 'Create my account'}
            </StyledButton>
            <Box sx={{ textAlign: 'center', marginTop: '16px' }}>
              <Link to="/login" style={{ 
                textDecoration: 'none', 
                color: '#9333EA', 
                fontWeight: 600,
                transition: 'color 0.2s ease',
                fontFamily: 'Manrope, sans-serif',
                fontSize: '14px'
              }}>
                Already have an account?
              </Link>
            </Box>
          </StyledForm>
        </StyledContainer>
      </MainContainer>
    </>
  );
}